import { Box, Stack } from "@mui/material";
import { styled } from "@mui/material/styles";

// Shared Tiptap prose styles
export const proseSx = (theme) => ({
  "& .tiptap": {
    outline: "none",
    "& > * + *": { marginTop: "0.5em" },
    "& p": { margin: 0, lineHeight: "1.5" },
    "& h1, & h2, & h3, & h4, & h5, & h6": { margin: "0.5em 0 0.25em" },
    "& ul, & ol": { paddingLeft: "1.5em", margin: "0.25em 0" },
    "& li > p": { margin: 0 },
    "& blockquote": {
      borderLeft: `4px solid ${theme.palette.hubColors.mainFocus}`,
      margin: "0.5em 0",
      paddingLeft: "1em",
      color: theme.palette.hubColors.grey,
    },
    "& code": {
      fontFamily: "monospace",
      backgroundColor: theme.palette.hubColors.greyLightest,
      color: "rgb(232, 145, 45)",
      borderRadius: 3,
      padding: "1px 4px",
      fontSize: "0.9em",
    },
    "& pre": {
      backgroundColor: theme.palette.hubColors.greyDark,
      color: theme.palette.hubColors.fontWhite,
      borderRadius: 6,
      padding: "12px 16px",
      margin: "0.5em 0",
      fontFamily: "monospace",
      fontSize: "0.85em",
      overflowX: "auto",
      "& code": {
        backgroundColor: "transparent",
        color: "inherit",
        padding: 0,
        borderRadius: 0,
      },
    },
    "& a": {
      color: theme.palette.hubColors.mainFocus,
      textDecoration: "underline",
      cursor: "pointer",
    },
    "& hr": {
      border: "none",
      borderTop: `1px solid ${theme.palette.hubColors.greyLight}`,
      margin: "1em 0",
    },
    "& img": {
      maxWidth: "100%",
      height: "auto",
      borderRadius: 4,
    },
    '& .mention, span[data-type="mention"], & .comment-mention': {
      color: theme.palette.hubColors.mainFocus,
      fontWeight: 600,
      backgroundColor: theme.palette.hubColors.mainFocusLighter2,
      borderRadius: 3,
      padding: "0 2px",
    },
    "& p.is-editor-empty:first-of-type::before": {
      content: "attr(data-placeholder)",
      color: theme.palette.hubColors.grey,
      pointerEvents: "none",
      float: "left",
      height: 0,
    },
  },
});

// HubEditor — compact input-like editor
export const StyledStack = styled(Stack)(({ theme }) => ({
  gap: "5px",
  ...proseSx(theme),
  "& .tiptap": {
    fontSize: 13,
    minHeight: "120px",
    padding: "10px 14px",
  },
}));

// HubEditorV2 — full-page document editor
export const StyledStackV2 = styled(Stack)(({ theme }) => ({
  gap: 0,
  maxWidth: "100%",
  overflow: "hidden",
  height: "100%",
  ...proseSx(theme),
  "& .tiptap": {
    fontSize: 14,
    lineHeight: "1.6",
    padding: "50px 0 200px 0",
    width: "635px",
    minWidth: "635px",
    margin: "0 auto",
    minHeight: "calc(100vh - 140px)",
  },
}));

// Editor chrome — border wrapper for compact editor
export const StyledEditorChrome = styled(Stack)(({ theme }) => ({
  border: `1px solid ${theme.palette.hubColors.greyLight}`,
  borderRadius: 8,
  overflow: "hidden",
  backgroundColor: theme.palette.hubColors.white,
  transition: "border-color 0.15s",
  ".ProseMirror-focused": {
    outline: "none",
  },
  minHeight: "150px",
  ...proseSx(theme),
}));

// Toolbar shared style
export const StyledToolbar = styled(Stack)(({ theme }) => ({
  flexDirection: "row",
  alignItems: "center",
  gap: "2px",
  padding: "4px 8px",
  borderBottom: `1px solid ${theme.palette.hubColors.greyLight}`,
  flexWrap: "wrap",
  backgroundColor: theme.palette.hubColors.greyLightest,
  "& .toolbar-btn": {
    minWidth: 28,
    width: 28,
    height: 28,
    padding: 0,
    borderRadius: 4,
    color: theme.palette.hubColors.greyDark,
    "&:hover": { backgroundColor: theme.palette.hubColors.greyLight },
    "&.is-active": {
      backgroundColor: theme.palette.hubColors.mainFocusLighter2,
      color: theme.palette.hubColors.mainFocus,
    },
  },
  "& .toolbar-divider": {
    width: 1,
    height: 20,
    backgroundColor: theme.palette.hubColors.greyLight,
    margin: "0 4px",
  },
}));

// Full-page toolbar
export const StyledToolbarV2 = styled(StyledToolbar)(({ theme }) => ({
  justifyContent: "center",
  padding: "7px 15px",
  borderBottom: `1px solid ${theme.palette.hubColors.greyLight}`,
  borderTop: `1px solid ${theme.palette.hubColors.greyLight}`,
  backgroundColor: theme.palette.hubColors.white,
}));

// Document content area
export const StyledDocumentArea = styled(Stack)(({ theme }) => ({
  flex: 1,
  overflow: "auto",
  display: "flex",
  justifyContent: "center",
  backgroundColor: theme.palette.hubColors.white,
}));

// Legacy compat
export const StyledHtmlBox = styled(Box)({
  "p, ol, h1, h2, h3, h4, h5, h6": { margin: 0 },
  "p, ol": { fontSize: 14 },
});
